// Ersteinrichtung: Radio anlegen, Musik hinein, auf Sendung. Erscheint nur, solange es keinen Zugang gibt.

import { useEffect, useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth/AuthProvider";
import { errorText } from "../components/streaming-ui";
import { AuthRahmen } from "../components/Logo";
import { LoadingScreen } from "../components/LoadingScreen";

const inputClass = "input mt-1.5";

export function SetupPage(): JSX.Element {
  const { refresh, user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"loading" | "open" | "closed">("loading");
  const [codeRequired, setCodeRequired] = useState(false);
  const [form, setForm] = useState({ radio_name: "", display_name: "", email: "", password: "", password2: "", setup_code: "" });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.setupStatus().then((s) => {
      setStatus(s.needs_setup ? "open" : "closed");
      setCodeRequired(Boolean(s.code_required));
    }).catch(() => setStatus("closed"));
  }, []);

  if (status === "loading") return <LoadingScreen />;
  // Schon eingerichtet: Wer angemeldet ist, landet im Dashboard, alle anderen beim Login.
  if (status === "closed") return <Navigate to={user ? "/dashboard" : "/login"} replace />;

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (form.password !== form.password2) {
      setError("Die beiden Passwörter sind nicht gleich.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.runSetup({ radio_name: form.radio_name, display_name: form.display_name || undefined, email: form.email, password: form.password, setup_code: form.setup_code || undefined });
      await refresh();
      navigate("/welcome", { replace: true });
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthRahmen
      breit
      titel="In drei Angaben auf Sendung."
      text="Radio anlegen, Musik hinein, fertig: Der Kanal sendet von selbst, sobald Titel da sind."
      punkte={["Radio anlegen", "Musik hochladen", "Reinhören"]}
    >
      <form onSubmit={submit} className="card space-y-4 p-6 text-primary shadow-lg sm:p-8">
        <div>
          <span className="badge badge-brand mb-3">Ersteinrichtung</span>
          <h1 className="text-2xl font-semibold tracking-tight">Willkommen bei der webradiosuite</h1>
          <p className="mt-1 text-sm text-muted">
            Drei Angaben, dann ist euer Radio angelegt und sendet, sobald Musik drin ist.
          </p>
        </div>
        <label className="block text-[0.8rem] font-medium text-secondary">
          Wie heißt euer Radio?
          <input className={inputClass} value={form.radio_name} onChange={(e) => setForm({ ...form, radio_name: e.target.value })} required minLength={2} maxLength={80} autoFocus placeholder="z. B. Radio Grüne Welle" />
        </label>
        <label className="block text-[0.8rem] font-medium text-secondary">
          Dein Name <span className="font-normal text-muted">(optional)</span>
          <input className={inputClass} value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} maxLength={80} autoComplete="name" />
        </label>
        <label className="block text-[0.8rem] font-medium text-secondary">
          E-Mail für die Anmeldung
          <input type="email" className={inputClass} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required autoComplete="email" />
        </label>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block text-[0.8rem] font-medium text-secondary">
            Passwort
            <input type="password" className={inputClass} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={10} autoComplete="new-password" />
          </label>
          <label className="block text-[0.8rem] font-medium text-secondary">
            Nochmal
            <input type="password" className={inputClass} value={form.password2} onChange={(e) => setForm({ ...form, password2: e.target.value })} required minLength={10} autoComplete="new-password" />
          </label>
        </div>
        <p className="text-xs text-muted">Mindestens 10 Zeichen. Dieser Zugang verwaltet die ganze Installation.</p>
        {codeRequired && (
          <label className="block text-[0.8rem] font-medium text-secondary">
            Einrichtungs-Code
            <input className={`${inputClass} font-mono uppercase`} value={form.setup_code} onChange={(e) => setForm({ ...form, setup_code: e.target.value })} required maxLength={12} />
            <span className="mt-1 block text-xs font-normal text-muted">
              Schutz davor, dass jemand anderes euer Radio zuerst einrichtet. Der Code steht im Log des Servers:
              <code className="ml-1 rounded bg-input px-1 py-0.5 text-[0.7rem]">docker compose logs api | grep Einrichtungs-Code</code>
            </span>
          </label>
        )}
        {error && <div className="callout callout-danger" role="alert">{error}</div>}
        <button type="submit" disabled={busy} className="btn btn-primary btn-lg w-full">
          {busy ? "Richte ein …" : "Radio anlegen"}
        </button>
      </form>
    </AuthRahmen>
  );
}
